import React, { useState, useEffect, FC, AnimationEvent, ChangeEvent, KeyboardEvent, ReactNode, useMemo } from 'react';

import useClassnames from 'hook/use-classnames';
import EyeIcon from 'component/icon/eye';
import EyeIconCrossed from 'component/icon/eye-crossed';
import IconEmail from 'component/icon/email';
import IconLink from 'component/icon/link';
import IconSearch from 'component/icon/search';
import Error from 'component/error';

import { IProps, TError, IPattern } from './types';
import style from './index.pcss';

const pattern: IPattern = {
    email   : /[-!#$%&'*+\/=?^`{}|~\w]+(\.[-!#$%&'*+\/=?^`{}|~\w]+)?@[a-z\d-]{0,61}(?:\..{2,})/i,
    password: /.+/
};

const Input: FC<IProps> = ({ direction = 'row', validation = true, autoComplete = 'on', isSmall = false, ...props}) => {
    const cn = useMemo(() => useClassnames(style, props.className, true), [props.className]);
    const [errorExternal, setErrorExternal] = useState<TError>(props.error || null);
    const [isValid, setIsValid] = useState<boolean>(false);
    const [value, setValue] = useState<string>(props.defaultValue || '');
    const [isAutoFill, setIsAutoFill] = useState<boolean>(false);
    const [type, setType] = useState<string>(props.type || 'text');
    const [errorInternal, setErrorInternal] = useState<TError>(null);
    const [isWatch, setIsWatch] = useState<boolean>(!!props.defaultValue);

    const checkValidity = (): boolean => {
        let newIsValid = true;
        let newErrorInternal: TError = null;

        if(props.required) {
            newIsValid = !!value;

            if(!newIsValid) {
                newErrorInternal = 'ошибка ввода';
            }
        }

        if(props.minLength) {
            newIsValid = value.length >= props.minLength;

            if(!newIsValid) {
                newErrorInternal = 'ошибка длины ввода';
            }
        }

        if(validation && newIsValid) {
            if(props.type && pattern[props.type]) {
                newIsValid = value.search(pattern[props.type]) !== -1;

                if(!newIsValid) {
                    newErrorInternal = 'ошибка';
                }
            }

            if(props.pattern && newIsValid) {
                newIsValid = value.search(props.pattern) !== -1;

                if(!newIsValid) {
                    newErrorInternal = props.patternError || 'Pattern error';
                }
            }
        }

        if(newIsValid && errorExternal) {
            newIsValid = false;
        }

        if(isAutoFill) {
            newIsValid = true;
            newErrorInternal = null;
        }

        setIsValid(newIsValid);
        setErrorInternal(newErrorInternal);

        return newIsValid;
    };

    useEffect((): void => {
        checkValidity();
    }, [isWatch]);

    useEffect(() => {
        props.registry.set(props.name, {
            setError: setErrorExternal,
            clear   : () => {
                setValue('');
                setIsWatch(false);
                setIsAutoFill(false);
            },
            isValid: checkValidity(),
            value,
            isAutoFill
        });
    }, [value, isAutoFill, props.pattern]);

    useEffect(() => {
        return () => {
            props.registry.remove(props.name);
        };
    }, []);

    useEffect(() => {
        if(props.defaultValue) {
            setValue(props.defaultValue);
        }
    }, [props.defaultValue]);

    useEffect(() => {
        const handler = props.registry.onChange();

        if(handler) {
            handler();
        }
    }, [isAutoFill, value, isValid]);

    const elLabel = (): ReactNode => {
        if(props.children) {
            return (
                <strong
                    className={cn('input__label', `input__label_${direction}`, {
                        'input__label_required': props.required,
                        'input__label_small': isSmall
                    })}
                >
                    {props.children}
                </strong>
            );
        }
    };

    const onAnimationStart = (e: AnimationEvent): void => {
        if(e.animationName === 'onInputAutoFillStart') {
            setIsAutoFill(true);
        }
    };

    const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
        setValue(e.target.value);

        if (!!props.onChange) {
            props.onChange(e);
        }

        if(errorExternal) {
            setErrorExternal(null);
        }

        if(isAutoFill) {
            setIsAutoFill(false);
        }
    };

    const onBlur = (): void => {
        if(!isWatch) {
            setIsWatch(true);
        }
    };

    const onKey = (e?: KeyboardEvent<HTMLInputElement>) => {
        if (e && props.onlyNumbers) {
            const allowedKeys = ['ArrowLeft', 'ArrowRight', 'Tab', 'Escape', 'Delete', 'Backspace'];
            const isAllowedKey = allowedKeys.includes(e.key);

            if (!isAllowedKey && (e.key < '0' || e.key > '9')) {
                e.preventDefault();
            }
        }
    };

    const onClickPassword = (): void => {
        if(props.type === 'password') {
            setType(type === 'password' ? 'text' : 'password');
        }
    };

    const elIconEye = (): ReactNode => {
        if(props.type === 'password') {
            if(type !== 'password') {
                return (
                    <EyeIconCrossed
                        width={24}
                        height={24}
                        onClick={onClickPassword}
                        className={cn('input__icon', 'input__icon_right', 'input__icon_eye')}
                    />
                );
            }

            return (
                <EyeIcon
                    width={24}
                    height={24}
                    onClick={onClickPassword}
                    className={cn('input__icon', 'input__icon_right', 'input__icon_eye')}
                />
            );
        }
    };

    const elError = (): ReactNode => {
        if((isWatch && errorInternal) || errorExternal) {
            return <Error elIcon={true} className={cn('input__error')}>{errorInternal || errorExternal}</Error>;
        }
    };

    const elIcon = useMemo(() => {
        switch(props.icon) {
            case 'email':
                return <IconEmail className={cn('input__icon')} />;
            case 'link':
                return <IconLink className={cn('input__icon')} />;
            case 'search':
                return <IconSearch className={cn('input__icon')} />;
        }
    }, [props.icon]);

    return (
        <label
            className={cn('input', `input_${direction}`)}
        >
            {elLabel()}
            <div className={cn('input__wrapper', {'input__wrapper_small': isSmall})}>
                {elIcon}
                <input
                    className={cn('input__field', {
                        'input__field_invalid': (isWatch && !isValid) || errorExternal,
                        'input__field_icon': props.icon,
                        'input__field_small': isSmall
                    })}
                    name={props.name}
                    autoComplete={autoComplete}
                    type={type}
                    disabled={props.disabled}
                    required={props.required}
                    placeholder={props.placeholder}
                    onAnimationStart={onAnimationStart}
                    value={value}
                    onChange={onChange}
                    onKeyDown={onKey}
                    onKeyUp={onKey}
                    autoFocus={props.autoFocus}
                    onBlur={onBlur}
                    tabIndex={props.tabIndex}
                    maxLength={props.maxLength}
                    min={props.min}
                    max={props.max}
                    readOnly={props.readonly}
                />
                {elIconEye()}
            </div>
            {elError()}
        </label>
    );
};

export default Input;
